import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Chip, FormControl, InputLabel, LinearProgress, MenuItem, Select, Tab, Tabs, TextField, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import useAuth from '../../hooks/useAuth';
import unifiedSocket from '../../services/unifiedSocket';
import {
  approveProductionScript,
  approveProductionEditorialReview,
  deleteProduction,
  getProduction,
  getProductionTracking,
  getProductionManualScriptPrompt,
  listVoiceProfiles,
  mintPublicAssetLink,
  rejectProductionEditorialReview,
  retryProduction,
  runProduction,
  submitProductionManualScript,
  updateProduction
} from '../../services/contentAiService';

const STAGE_GROUPS = [
  { key: 'research', label: 'Pesquisa', pipelineKeys: ['research'] },
  { key: 'knowledge_package', label: 'Pacote de Conhecimento', pipelineKeys: ['knowledge_package'] },
  { key: 'script', label: 'Roteiro', pipelineKeys: ['script'] },
  { key: 'storyboard', label: 'Storyboard', pipelineKeys: ['storyboard'] },
  { key: 'images', label: 'Assets', pipelineKeys: ['images'] },
  { key: 'narration', label: 'Narração', pipelineKeys: ['narration'] },
  { key: 'video', label: 'Vídeo', pipelineKeys: ['video'] },
  { key: 'thumbnail', label: 'Thumbnail', pipelineKeys: ['thumbnail'] },
  { key: 'editorial', label: 'Revisão Editorial', pipelineKeys: ['editorial'] },
  { key: 'upload', label: 'Upload', pipelineKeys: ['upload'] }
];

const PIPELINE_PRIORITY = [
  'research',
  'knowledge_package',
  'script',
  'storyboard',
  'images',
  'narration',
  'video',
  'thumbnail',
  'editorial',
  'upload'
];

const INTERNAL_STAGE_TO_TIMELINE_KEY = {
  idea: 'research',
  research: 'research',
  knowledge_package: 'knowledge_package',
  generate_script: 'script',
  storyboard_engine: 'storyboard',
  generate_images: 'images',
  cinematic_scene_planner: 'storyboard',
  visual_excellence: 'images',
  narrative_excellence: 'script',
  cognitive_engagement: 'script',
  narration_excellence: 'narration',
  global_documentary_inspector: 'editorial',
  documentary_intelligence: 'editorial',
  select_scenes: 'images',
  generate_narration: 'narration',
  process_audio: 'narration',
  approve_assets: 'images',
  build_timeline: 'video',
  generate_scene_videos: 'video',
  render_video: 'video',
  preview_render: 'video',
  generate_thumbnail: 'thumbnail',
  audit: 'editorial',
  editorial_review: 'editorial',
  upload_youtube: 'upload',
  publish: 'upload',
  analytics_initial: 'upload'
};

function fmtDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function safeParseJson(text) {
  try {
    return JSON.parse(String(text || ''));
  } catch {
    return null;
  }
}

function countWords(text) {
  return String(text || '').trim().split(/\s+/g).filter(Boolean).length;
}

function displayText(value, keys = []) {
  if (typeof value === 'string' || typeof value === 'number') {
    const text = String(value).replace(/\s+/g, ' ').trim();
    return text && text !== '[object Object]' ? text : '';
  }
  if (!value || typeof value !== 'object') return '';
  const candidates = [...keys, 'title', 'name', 'nome', 'label', 'term', 'summary', 'resumo', 'value'];
  for (const key of candidates) {
    const text = displayText(value[key]);
    if (text) return text;
  }
  return '';
}

function buildNarrationFallback(scriptJson) {
  const data = scriptJson && typeof scriptJson === 'object' ? scriptJson : null;
  if (!data) return '';
  const script = data.script && typeof data.script === 'object' ? data.script : {};
  const canonicalChapters = Array.isArray(script.chapters) ? script.chapters : [];
  const values = canonicalChapters.length
    ? [script.hook, script.intro, ...canonicalChapters.map(chapter => chapter?.text), script.conclusion, script.cta, script.farewell]
    : [script.hook, script.intro, script.body, script.outro];
  return values
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .join('\n\n');
}

function normalizeManualScriptDraft(scriptJson) {
  const data = scriptJson && typeof scriptJson === 'object' ? { ...scriptJson } : null;
  if (!data) return null;
  const narrationText = String(data.narrationText || '').trim() || buildNarrationFallback(data);
  if (narrationText) data.narrationText = narrationText;
  return data;
}

function getStatusMeta(status) {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'success' || normalized === 'completed' || normalized === 'published') {
    return { label: 'Concluído', color: 'success', accent: 'success.main' };
  }
  if (normalized === 'running' || normalized === 'processing' || normalized === 'rendering' || normalized === 'publishing' || normalized === 'retrying') {
    return { label: 'Em andamento', color: 'info', accent: 'info.main' };
  }
  if (normalized === 'failed') {
    return { label: 'Falhou', color: 'error', accent: 'error.main' };
  }
  if (normalized === 'awaiting_approval') {
    return { label: 'Aguardando ação', color: 'warning', accent: 'warning.main' };
  }
  return { label: 'Pendente', color: 'default', accent: 'text.disabled' };
}

function getTimelineStageLabel(stageKey) {
  const timelineKey = INTERNAL_STAGE_TO_TIMELINE_KEY[String(stageKey || '').trim().toLowerCase()] || String(stageKey || '').trim().toLowerCase();
  return STAGE_GROUPS.find((item) => item.key === timelineKey)?.label || 'Aguardando';
}

function formatErrorLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (!/^[a-z0-9_.:-]+$/i.test(raw)) return raw;
  return raw
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function TrackingPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const productionId = Number(id || 0) || 0;
  const { user: authUser } = useAuth();
  const currentUser = authUser?.user || authUser || null;
  const token = authUser?.token || null;
  const socketUserId = currentUser?.id || null;
  const companyId = currentUser?.companyId || 1;

  const [loading, setLoading] = useState(false);
  const [prod, setProd] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [voiceProfileIdDraft, setVoiceProfileIdDraft] = useState('');
  const [manualPrompt, setManualPrompt] = useState('');
  const [manualScriptText, setManualScriptText] = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('tracking');
  const [editorialComment, setEditorialComment] = useState('');
  const [editorialReason, setEditorialReason] = useState('');
  const [editorialSubmitting, setEditorialSubmitting] = useState('');
  const [assetPreviewUrls, setAssetPreviewUrls] = useState({});
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const thumbnailInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!productionId) return;
    setLoading(true);
    try {
      let resp;
      try { resp = await getProduction(productionId); }
      catch (detailError) {
        resp = await getProductionTracking(productionId);
        if (resp?.data) resp.data.detailLoadWarning = detailError?.message || 'Detalhes avançados indisponíveis';
      }
      if (!resp?.data || Number(resp.data.id) !== productionId) throw new Error('API não retornou os detalhes deste projeto');
      setProd(resp.data);
      setLoadError('');
    } catch (e) {
      setLoadError(e?.response?.data?.message || e?.message || 'Falha ao carregar projeto');
    } finally {
      setLoading(false);
    }
  }, [productionId]);

  useEffect(() => {
    setProd(null);
    setLoadError('');
    load();
  }, [load]);

  // O websocket pode cair; a atualização periódica mantém status e falhas visíveis.
  useEffect(() => {
    if (!productionId) return undefined;
    const timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 15000);
    return () => clearInterval(timer);
  }, [productionId, load]);

  useEffect(() => {
    if (!productionId) return;
    listVoiceProfiles()
      .then((resp) => setVoiceProfiles(Array.isArray(resp?.data) ? resp.data : []))
      .catch(() => {});
  }, [productionId]);

  useEffect(() => {
    const currentVoiceId = prod?.metaJson?.voiceProfileId;
    if (currentVoiceId != null) setVoiceProfileIdDraft(String(currentVoiceId));
  }, [prod?.metaJson?.voiceProfileId]);

  useEffect(() => {
    const scriptJson = prod?.metaJson?.scriptJson;
    if (!scriptJson || manualScriptText.trim()) return;
    try {
      setManualScriptText(JSON.stringify(scriptJson, null, 2));
    } catch {}
  }, [prod?.metaJson?.scriptJson, manualScriptText]);

  const stages = useMemo(() => {
    const items = Array.isArray(prod?.timeline) ? prod.timeline : [];
    return items
      .slice()
      .sort((a, b) => {
        const ai = PIPELINE_PRIORITY.indexOf(String(a?.stageKey || ''));
        const bi = PIPELINE_PRIORITY.indexOf(String(b?.stageKey || ''));
        return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
      });
  }, [prod?.timeline]);

  const stageByKey = useMemo(() => {
    const map = new Map();
    stages.forEach((item) => {
      const key = String(item?.stageKey || '').trim();
      if (key) map.set(key, item);
    });
    return map;
  }, [stages]);

  const rawStageByKey = useMemo(() => {
    const rows = Array.isArray(prod?.stages) ? prod.stages : [];
    const map = new Map();
    rows.forEach((item) => {
      const key = String(item?.stageKey || '').trim();
      if (key) map.set(key, item);
    });
    return map;
  }, [prod?.stages]);

  const currentTimelineStageKey = useMemo(() => (
    INTERNAL_STAGE_TO_TIMELINE_KEY[String(prod?.currentStageKey || '').trim().toLowerCase()]
      || String(prod?.currentStageKey || '').trim().toLowerCase()
      || null
  ), [prod?.currentStageKey]);

  const groupedStages = useMemo(() => (
    STAGE_GROUPS.map((group) => {
      const stageRows = group.pipelineKeys
        .map((key) => stageByKey.get(key))
        .filter(Boolean);
      const statuses = stageRows
        .map((item) => String(item?.status || '').trim().toLowerCase())
        .filter(Boolean);
      const failedStage = stageRows.find((item) => String(item?.status || '').trim().toLowerCase() === 'failed');
      const lastErrorLog = stageRows
        .flatMap((item) => Array.isArray(item?.logs) ? item.logs : [])
        .find((log) => String(log?.level || '').trim().toLowerCase() === 'error');
      let status = 'pending';
      if (!statuses.length) status = 'pending';
      else if (statuses.some((item) => item === 'failed')) status = 'failed';
      else if (statuses.some((item) => item === 'awaiting_approval')) status = 'awaiting_approval';
      else if (statuses.some((item) => ['running', 'processing', 'rendering', 'publishing', 'retrying'].includes(item))) status = 'running';
      else if (statuses.every((item) => ['success', 'completed', 'published'].includes(item))) status = 'success';
      else if (statuses.some((item) => ['success', 'completed', 'published'].includes(item))) status = 'running';
      return {
        ...group,
        status,
        current: group.pipelineKeys.includes(String(currentTimelineStageKey || '')),
        errorMessage: String(failedStage?.errorMessage || '').trim(),
        lastErrorLog: String(lastErrorLog?.message || '').trim()
      };
    })
  ), [currentTimelineStageKey, stageByKey]);

  const visibleErrorMessage = useMemo(() => {
    const productionError = String(prod?.errorMessage || '').trim();
    if (productionError) return productionError;
    const failedStage = groupedStages.find((stage) => stage.status === 'failed' && stage.errorMessage);
    if (failedStage?.errorMessage) return failedStage.errorMessage;
    const failedRawStage = Array.from(rawStageByKey.values()).find((stage) => String(stage?.status || '').trim().toLowerCase() === 'failed' && String(stage?.errorMessage || '').trim());
    return String(failedRawStage?.errorMessage || '').trim();
  }, [prod?.errorMessage, groupedStages, rawStageByKey]);

  const nextRetryStageKey = useMemo(() => {
    const failedRaw = (Array.isArray(prod?.stages) ? [...prod.stages] : []).reverse().find((item) => String(item?.status || '').trim().toLowerCase() === 'failed');
    if (failedRaw?.stageKey) return String(failedRaw.stageKey);
    const failed = stages.find((item) => String(item?.status || '').trim().toLowerCase() === 'failed');
    if (failed?.stageKey) return String((Array.isArray(failed?.metaJson?.internalStageKeys) && failed.metaJson.internalStageKeys[0]) || prod?.currentStageKey || failed.stageKey);
    const current = String(currentTimelineStageKey || '').trim();
    if (current) return current;
    return null;
  }, [stages, currentTimelineStageKey, prod?.stages, prod?.currentStageKey]);

  const needsManualScript = useMemo(() => {
    const generateScriptStage = rawStageByKey.get('generate_script');
    const status = String(generateScriptStage?.status || '').trim().toLowerCase();
    const mode = String(prod?.metaJson?.scriptInputMode || '').trim().toLowerCase();
    if (status !== 'awaiting_approval') return false;
    if (generateScriptStage?.metaJson?.scriptReviewRequired) return true;
    return mode === 'manual';
  }, [rawStageByKey, prod?.metaJson?.scriptInputMode]);

  const isAutoScriptReview = useMemo(() => {
    const generateScriptStage = rawStageByKey.get('generate_script');
    return String(generateScriptStage?.status || '').trim().toLowerCase() === 'awaiting_approval'
      && generateScriptStage?.metaJson?.scriptReviewRequired === true
      && generateScriptStage?.metaJson?.autoGenerated === true;
  }, [rawStageByKey]);

  useEffect(() => {
    const ids = new Set();
    const erVid = prod?.editorialReview?.video?.videoAsset?.id;
    const erThumb = prod?.editorialReview?.video?.thumbnailAsset?.id;
    const vid = prod?.video?.outputVideoAssetId ?? prod?.video?.videoAssetId ?? prod?.metaJson?.outputVideoAssetId ?? prod?.metaJson?.manualFullVideoAssetId ?? null;
    const thu = prod?.video?.thumbnailAssetId ?? prod?.metaJson?.manualThumbnailAssetId ?? prod?.metaJson?.thumbnailAssetId ?? null;
    [erVid, erThumb, vid, thu].forEach((x) => {
      const n = Number(x); if (Number.isFinite(n) && n > 0) ids.add(n);
    });
    if (!ids.size) return undefined;
    Promise.all([...ids].map(async (assetId) => {
      try {
        const resp = await mintPublicAssetLink(assetId, 240);
        return [assetId, resp?.data?.url || resp?.url || null];
      } catch {
        return [assetId, null];
      }
    })).then((rows) => {
      setAssetPreviewUrls((prev) => {
        const next = { ...(prev || {}) };
        for (const [assetId, url] of rows) {
          if (url) next[String(assetId)] = url;
        }
        return next;
      });
    }).catch(() => {});
    return undefined;
  }, [prod?.editorialReview?.video?.videoAsset?.id, prod?.editorialReview?.video?.thumbnailAsset?.id, prod?.video?.outputVideoAssetId, prod?.video?.thumbnailAssetId, prod?.video?.videoAssetId, prod?.metaJson?.outputVideoAssetId, prod?.metaJson?.manualThumbnailAssetId, prod?.metaJson?.thumbnailAssetId, prod?.metaJson?.manualFullVideoAssetId]);

  const needsVoiceProfile = useMemo(() => {
    const narrationStage = rawStageByKey.get('generate_narration');
    const status = String(narrationStage?.status || '').trim().toLowerCase();
    const errorMessage = String(narrationStage?.errorMessage || '').trim();
    return (status === 'failed' && errorMessage === 'voice_profile_required')
      || (status === 'awaiting_approval' && narrationStage?.metaJson?.voiceProfileRequired === true);
  }, [rawStageByKey]);

  const needsEditorialReview = useMemo(() => {
    if (prod?.editorialReview?.canDecide === true) return true;
    const s = String(prod?.status || '').trim().toLowerCase();
    const c = String(prod?.currentStageKey || '').trim().toLowerCase();
    const awaitingStatuses = ['awaiting_approval', 'pending_approval', 'approval_needed'];
    return awaitingStatuses.includes(s) && c === 'editorial_review';
  }, [prod?.editorialReview?.canDecide, prod?.status, prod?.currentStageKey]);

  const editorialStageMeta = useMemo(() => {
    const er = prod?.editorialReview || {};
    const overallScore = er?.approval?.editorialScore?.overallScore
      ?? er?.approval?.qualityReport?.overallScore
      ?? er?.approval?.approvalStatus?.overallScore
      ?? er?.editorialStage?.meta?.overallScore
      ?? null;
    const approvalStatus = er?.approval?.approvalStatus || null;
    const rawIssues = Array.isArray(er?.approval?.issues) ? er.approval.issues : [];
    const issueString = (issue) => {
      if (issue == null) return '';
      if (typeof issue === 'string') return issue;
      const parts = [];
      const push = (val) => {
        const s = String(val || '').trim();
        if (s && s !== '[object Object]') parts.push(s);
      };
      push(issue.message); push(issue.description); push(issue.title); push(issue.detail); push(issue.summary);
      push(issue.action); push(issue.suggestion); push(issue.reason); push(issue.rule); push(issue.text);
      if (Array.isArray(issue.parts)) for (const p of issue.parts) push(p);
      if (!parts.length) {
        try { push(JSON.stringify(issue).replace(/^\{/, '').replace(/\}$/, '').slice(0, 200)); } catch { /* noop */ }
      }
      return parts.filter((p, i, arr) => arr.indexOf(p) === i).join(' · ');
    };
    const issues = rawIssues.map((issue, idx) => {
      if (issue == null) return null;
      const severity = String(issue?.severity || issue?.level || issue?.type || issue?.priority || 'info');
      const code = String(issue?.code || issue?.key || issue?.id || issue?.rule || '');
      const message = issueString(issue) || `Problema #${idx + 1}`;
      const short = message.length > 140 ? `${message.slice(0, 140)}…` : message;
      return {
        id: code || `issue-${idx}`, severity, key: `issue-${idx}-${code || 'x'}`, label: code ? `[${severity}] ${code} — ${short}` : `[${severity}] ${short}`
      };
    }).filter(Boolean);

    const allStages = [
      ...(Array.isArray(prod?.stages) ? prod.stages : []),
      ...(Array.isArray(prod?.pipelineStages) ? prod.pipelineStages : [])
    ];
    const lastCompletedMeta = (() => {
      const completed = allStages
        .filter((s) => String(s?.status || '').toLowerCase() === 'completed' && ((s.metaJson || s.meta) && typeof (s.metaJson || s.meta) === 'object'))
        .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
      return completed.length ? (completed[0].metaJson || completed[0].meta || {}) : {};
    })();
    const stageVid = allStages.reduce((acc, s) => {
      if (acc) return acc;
      const m = (s.metaJson || s.meta) || {};
      const n = Number(m.outputVideoAssetId || m.videoAssetId || m.finalVideoAssetId || 0);
      return Number.isFinite(n) && n > 0 ? n : acc;
    }, null);
    const stageThumb = allStages.reduce((acc, s) => {
      if (acc) return acc;
      const m = (s.metaJson || s.meta) || {};
      const n = Number(m.thumbnailAssetId || m.bestThumbnailAssetId || m.finalThumbnailAssetId || 0);
      return Number.isFinite(n) && n > 0 ? n : acc;
    }, null);

    const video = er?.video || {};
    const va = video.videoAsset || null;
    const ta = video.thumbnailAsset || null;
    const fallbackVidId =
      va?.id ??
      video.outputVideoAssetId ??
      stageVid ??
      lastCompletedMeta.outputVideoAssetId ??
      lastCompletedMeta.videoAssetId ??
      prod?.video?.outputVideoAssetId ??
      prod?.video?.videoAssetId ??
      prod?.metaJson?.outputVideoAssetId ??
      prod?.metaJson?.manualFullVideoAssetId ??
      prod?.metaJson?.finalVideoAssetId ??
      null;
    const fallbackThumbId =
      ta?.id ??
      video.thumbnailAssetId ??
      stageThumb ??
      lastCompletedMeta.thumbnailAssetId ??
      prod?.video?.thumbnailAssetId ??
      prod?.metaJson?.manualThumbnailAssetId ??
      prod?.metaJson?.thumbnailAssetId ??
      prod?.metaJson?.bestThumbnailAssetId ??
      null;
    const vaId = fallbackVidId ? Number(fallbackVidId) : null;
    const taId = fallbackThumbId ? Number(fallbackThumbId) : null;

    const authedVideoUrl = Number.isFinite(vaId) && vaId > 0 ? `/api/media-assets/${vaId}` : '';
    const authedThumbUrl = Number.isFinite(taId) && taId > 0 ? `/api/media-assets/${taId}` : '';

    const previewVideoUrl = Number.isFinite(vaId) && assetPreviewUrls[String(vaId)] ? assetPreviewUrls[String(vaId)] : '';
    const previewThumbUrl = Number.isFinite(taId) && assetPreviewUrls[String(taId)] ? assetPreviewUrls[String(taId)] : '';

    const resolvedVideoUrl = previewVideoUrl || va?.url || va?.authedUrl || authedVideoUrl || '';
    const resolvedThumbUrl = previewThumbUrl || ta?.url || ta?.authedUrl || authedThumbUrl || '';

    return {
      canDecide: Boolean(er?.canDecide),
      overallScore: overallScore != null ? Number(overallScore) : null,
      approvalStatus,
      issues,
      videoAsset: va,
      thumbnailAsset: ta,
      resolvedVideoUrl,
      resolvedThumbUrl,
      debugVideoAssetId: vaId,
      debugThumbAssetId: taId,
      debugPreviewVideo: previewVideoUrl ? 'ok' : '—',
      debugPreviewThumb: previewThumbUrl ? 'ok' : '—',
      durationSeconds: video.durationSeconds || null,
      resolution: video.resolution || null,
      hasSubtitles: Boolean(video.hasSubtitles),
      stageStatus: er?.editorialStage?.status || '',
      trigger: String(er?.editorialStage?.meta?.trigger || '')
    };
  }, [
    prod?.editorialReview,
    prod?.video,
    prod?.metaJson,
    prod?.stages,
    prod?.pipelineStages,
    assetPreviewUrls
  ]);

  const scriptStats = useMemo(() => {
    const parsed = normalizeManualScriptDraft(safeParseJson(manualScriptText));
    if (!parsed) return null;
    const narrationText = String(parsed?.narrationText || '').trim();
    return {
      words: countWords(narrationText),
      chars: narrationText.length
    };
  }, [manualScriptText]);

  const researchData = useMemo(() => {
    const researchJson = prod?.contentIntelligence?.researchJson || prod?.metaJson?.research;
    if (!researchJson || typeof researchJson !== 'object') return null;
    return {
      plan: researchJson.plan || null,
      sources: Array.isArray(researchJson.collectedSources) ? researchJson.collectedSources : [],
      validatedSources: Array.isArray(researchJson.validatedSources) ? researchJson.validatedSources : [],
      discardedSources: Array.isArray(researchJson.discardedSources) ? researchJson.discardedSources : [],
      bibliography: Array.isArray(researchJson.bibliography) ? researchJson.bibliography : [],
      timeline: Array.isArray(researchJson.timeline) ? researchJson.timeline : [],
      curiosities: Array.isArray(researchJson.curiosities) ? researchJson.curiosities : [],
      facts: Array.isArray(researchJson.facts) ? researchJson.facts : [],
      statistics: Array.isArray(researchJson.statistics) ? researchJson.statistics : [],
      report: researchJson.researchReport || null,
      confidence: researchJson.researchConfidence || null,
      qualityScores: researchJson.qualityScores || null,
      metrics: researchJson.researchMetrics || null
    };
  }, [prod?.contentIntelligence?.researchJson, prod?.metaJson?.research]);

  const knowledgePackageData = useMemo(() => {
    const knowledgePackage = prod?.metaJson?.knowledgePackage;
    if (!knowledgePackage || typeof knowledgePackage !== 'object') return null;
    return {
      topic: knowledgePackage.topic || null,
      executiveSummary: knowledgePackage.executiveSummary || null,
      timeline: Array.isArray(knowledgePackage.timeline) ? knowledgePackage.timeline : [],
      facts: Array.isArray(knowledgePackage.facts) ? knowledgePackage.facts : [],
      curiosities: Array.isArray(knowledgePackage.curiosities) ? knowledgePackage.curiosities : [],
      entities: Array.isArray(knowledgePackage.entities) ? knowledgePackage.entities : [],
      locations: Array.isArray(knowledgePackage.locations) ? knowledgePackage.locations : [],
      characters: Array.isArray(knowledgePackage.characters) ? knowledgePackage.characters : [],
      statistics: Array.isArray(knowledgePackage.statistics) ? knowledgePackage.statistics : [],
      glossary: Array.isArray(knowledgePackage.glossary) ? knowledgePackage.glossary : [],
      storyHooks: Array.isArray(knowledgePackage.storyHooks) ? knowledgePackage.storyHooks : [],
      visualOpportunities: Array.isArray(knowledgePackage.visualOpportunities) ? knowledgePackage.visualOpportunities : [],
      bibliography: Array.isArray(knowledgePackage.bibliography) ? knowledgePackage.bibliography : [],
      sources: Array.isArray(knowledgePackage.sources) ? knowledgePackage.sources : [],
      confidence: knowledgePackage.confidence || null,
      qualityScores: knowledgePackage.qualityScores || null,
      topicRelevanceAudit: knowledgePackage.topicRelevanceAudit || null,
      validation: knowledgePackage.validation || null,
      version: knowledgePackage.version || prod?.metaJson?.knowledgeVersion || null,
      updatedAt: knowledgePackage.updatedAt || prod?.metaJson?.knowledgeUpdatedAt || null
    };
  }, [prod?.metaJson?.knowledgePackage, prod?.metaJson?.knowledgeVersion, prod?.metaJson?.knowledgeUpdatedAt]);

  const editorialProfileData = useMemo(() => {
    const meta = prod?.metaJson;
    if (!meta || typeof meta !== 'object' || !meta.editorialProfile) return null;
    return {
      key: String(meta.editorialProfile || '').trim(),
      label: String(meta.editorialProfileLabel || meta.editorialProfile || '').trim(),
      summary: String(meta.editorialProfileSummary || '').trim(),
      productionStrategy: String(meta.productionStrategy || '').trim(),
      narrativeStyle: String(meta.narrativeStyle || '').trim(),
      visualStyle: String(meta.visualStyle || '').trim(),
      seoStyle: String(meta.seoStyle || '').trim(),
      thumbnailStyle: String(meta.thumbnailStyle || '').trim()
    };
  }, [
    prod?.metaJson?.editorialProfile,
    prod?.metaJson?.editorialProfileLabel,
    prod?.metaJson?.editorialProfileSummary,
    prod?.metaJson?.productionStrategy,
    prod?.metaJson?.narrativeStyle,
    prod?.metaJson?.visualStyle,
    prod?.metaJson?.seoStyle,
    prod?.metaJson?.thumbnailStyle
  ]);

  useEffect(() => {
    if (!needsManualScript || isAutoScriptReview || manualPrompt.trim() || !productionId) return;
    setManualLoading(true);
    getProductionManualScriptPrompt(productionId)
      .then((resp) => setManualPrompt(String(resp?.data?.prompt || '')))
      .catch((e) => toast.error(e?.message || 'Falha ao carregar prompt'))
      .finally(() => setManualLoading(false));
  }, [needsManualScript, isAutoScriptReview, manualPrompt, productionId]);

  useEffect(() => {
    if (!token || !socketUserId) return;
    unifiedSocket.connect({ id: socketUserId, companyId, token });
    const socket = unifiedSocket.getSocket?.();
    if (!socket) return;

    const onUpdated = (payload) => {
      if (Number(payload?.productionId || 0) !== productionId) return;
      load().catch(() => {});
    };
    const onLog = (payload) => {
      if (Number(payload?.productionId || 0) !== productionId) return;
      setProd((prev) => ({
        ...(prev || {}),
        logs: [payload?.log, ...((prev?.logs || []).filter(Boolean))].slice(0, 5)
      }));
    };

    socket.on('production.updated', onUpdated);
    socket.on('production.log', onLog);
    return () => {
      try { socket.off('production.updated', onUpdated); } catch {}
      try { socket.off('production.log', onLog); } catch {}
    };
  }, [token, socketUserId, companyId, productionId, load]);

  const onRun = useCallback(async () => {
    if (!productionId) return;
    setLoading(true);
    try {
      await runProduction(productionId);
      toast.success('Processamento iniciado');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao iniciar');
    } finally {
      setLoading(false);
    }
  }, [productionId, load]);

  const onRetry = useCallback(async () => {
    if (!productionId || !nextRetryStageKey) return;
    setLoading(true);
    try {
      await retryProduction(productionId, { stageKey: nextRetryStageKey });
      toast.success('Tentativa reenfileirada');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao tentar novamente');
    } finally {
      setLoading(false);
    }
  }, [productionId, nextRetryStageKey, load]);

  const onDelete = useCallback(async () => {
    if (!productionId) return;
    const confirmed = window.confirm(`Excluir o projeto #${productionId}?`);
    if (!confirmed) return;
    setLoading(true);
    try {
      await deleteProduction(productionId);
      toast.success('Projeto excluído');
      navigate('/production');
    } catch (e) {
      toast.error(e?.message || 'Falha ao excluir');
    } finally {
      setLoading(false);
    }
  }, [productionId, navigate]);

  const onSaveVoice = useCallback(async () => {
    if (!productionId) return;
    const voiceProfileId = Number(voiceProfileIdDraft || 0) || 0;
    if (!voiceProfileId) {
      toast.error('Selecione um narrador');
      return;
    }
    setLoading(true);
    try {
      await updateProduction(productionId, { voiceProfileId });
      await retryProduction(productionId, { stageKey: 'generate_narration' });
      toast.success('Narrador salvo');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar narrador');
    } finally {
      setLoading(false);
    }
  }, [productionId, voiceProfileIdDraft, load]);

  const onApproveScript = useCallback(async () => {
    if (!productionId) return;
    setLoading(true);
    try {
      await approveProductionScript(productionId);
      toast.success('Roteiro aprovado');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao aprovar roteiro');
    } finally {
      setLoading(false);
    }
  }, [productionId, load]);

  const onSaveManualScript = useCallback(async () => {
    if (!productionId) return;
    const parsed = safeParseJson(manualScriptText);
    if (!parsed || typeof parsed !== 'object') {
      toast.error('JSON inválido');
      return;
    }
    const normalized = normalizeManualScriptDraft(parsed);
    const normalizedText = JSON.stringify(normalized || {}, null, 2);
    setManualLoading(true);
    try {
      setManualScriptText(normalizedText);
      await submitProductionManualScript(productionId, { scriptJsonText: normalizedText });
      toast.success('Roteiro salvo');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar roteiro');
    } finally {
      setManualLoading(false);
    }
  }, [productionId, manualScriptText, load]);

  const onApproveEditorial = useCallback(async () => {
    if (!productionId) return;
    setEditorialSubmitting('approve');
    try {
      const comment = String(editorialComment || '').trim() || null;
      await approveProductionEditorialReview(productionId, { comment });
      toast.success('Revisão editorial aprovada');
      setEditorialComment('');
      setEditorialReason('');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao aprovar revisão');
    } finally {
      setEditorialSubmitting('');
    }
  }, [productionId, editorialComment, load]);

  const onRejectEditorial = useCallback(async () => {
    if (!productionId) return;
    const reason = String(editorialReason || '').trim();
    if (!reason) {
      toast.error('Informe o motivo da recusa');
      return;
    }
    setEditorialSubmitting('reject');
    try {
      const comment = String(editorialComment || '').trim() || null;
      await rejectProductionEditorialReview(productionId, { reason, comment });
      toast.success('Revisão editorial recusada');
      setEditorialComment('');
      setEditorialReason('');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao recusar revisão');
    } finally {
      setEditorialSubmitting('');
    }
  }, [productionId, editorialComment, editorialReason, load]);

  const onReplaceThumbnail = useCallback(async () => {
    const file = thumbnailInputRef.current?.files?.[0] || null;
    if (!file) return;
    const videoId = prod?.video?.id != null ? Number(prod.video.id) : null;
    if (!videoId) {
      toast.error('Vídeo ainda não criado');
      return;
    }
    setUploadingThumbnail(true);
    try {
      const fd = new FormData();
      fd.append('thumbnail', file);
      const resp = await fetch(`/api/videos/${videoId}/thumbnail`, {
        method: 'POST',
        credentials: 'include',
        body: fd
      }).then(async (r) => {
        if (!r.ok) {
          let msg = 'Falha ao enviar thumbnail';
          try { const er = await r.json(); if (er?.message) msg = er.message; } catch {}
          throw new Error(msg);
        }
        return r.json();
      });
      const newAssetId = resp?.data?.thumbnailAssetId ? Number(resp.data.thumbnailAssetId) : null;
      if (newAssetId && Number.isFinite(newAssetId) && newAssetId > 0) {
        try {
          const minted = await mintPublicAssetLink(newAssetId, 240);
          if (minted?.url) {
            setAssetPreviewUrls((prev) => ({ ...prev, [String(newAssetId)]: minted.url }));
          }
        } catch {}
      }
      toast.success('Thumbnail substituída');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Falha ao substituir thumbnail');
    } finally {
      setUploadingThumbnail(false);
      if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
    }
  }, [prod, load]);

  if (!productionId) {
    return (
      <Box p={3}>
        <Typography>Projeto inválido.</Typography>
      </Box>
    );
  }

  const progressRaw = Number(prod?.progressPct || 0);
  const progress = Math.max(0, Math.min(100, Math.round(progressRaw <= 1 ? progressRaw * 100 : progressRaw)));
  const etaMinutes = !['failed', 'completed', 'published'].includes(String(prod?.status || '').toLowerCase()) && prod?.metaJson?.estimatedRemainingMs != null
    ? Math.max(1, Math.round(Number(prod.metaJson.estimatedRemainingMs) / 60000))
    : null;
  const language = String(prod?.metaJson?.language || prod?.metaJson?.scriptJson?.language || 'pt-BR').trim() || 'pt-BR';
  const minutes = Number(prod?.metaJson?.minutes || 0) || 8;
  const stageMeta = getStatusMeta(prod?.status || '');

  if (!prod) {
    return (
      <Box p={{ xs: 2, md: 3 }} display="grid" gap={2}>
        <Typography variant="h4">Projeto #{productionId}</Typography>
        {loadError ? (
          <Typography color="error.main">Falha ao carregar os dados reais do projeto: {loadError}</Typography>
        ) : (
          <Typography>Carregando dados do projeto...</Typography>
        )}
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={() => navigate('/production')}>Voltar</Button>
          <Button variant="contained" onClick={load} disabled={loading}>Tentar carregar novamente</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, md: 3 }} display="grid" gap={3}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box display="grid" gap={0.75}>
          {loadError ? <Typography color="error.main">Falha ao atualizar: {loadError}</Typography> : null}
          {prod?.detailLoadWarning ? <Typography color="warning.main">Dados básicos disponíveis. Detalhes avançados não carregaram: {prod.detailLoadWarning}</Typography> : null}
          <Typography variant="h4">{String(prod?.topic || prod?.title || `Projeto #${productionId}`).trim()}</Typography>
          <Typography variant="body1" color="text.secondary">
            Projeto #{productionId} · {language} · {minutes} min
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip label={stageMeta.label} color={stageMeta.color} />
            <Chip label={`Etapa atual: ${getTimelineStageLabel(prod?.currentStageKey)}`} />
            <Chip label={etaMinutes != null ? `ETA: ${etaMinutes} min` : 'ETA: calculando'} />
            {editorialProfileData?.label ? <Chip label={`Perfil: ${editorialProfileData.label}`} /> : null}
          </Box>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="outlined" onClick={() => navigate('/production')}>Voltar</Button>
          <Button variant="outlined" onClick={load} disabled={loading}>Atualizar</Button>
          {function () {
            var _k = String((prod && prod.currentStageKey) || '').trim().toLowerCase();
            if (_k === 'generate_images' || _k === 'select_scenes' || _k === 'approve_assets') {
              return (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => navigate('/production/' + String(productionId) + '/review')}
                  startIcon={<RefreshIcon fontSize="small" />}
                >
                  Central de Aprovação
                </Button>
              );
            }
            return null;
          }()}
          <Button variant="outlined" onClick={onRetry} disabled={loading || !nextRetryStageKey}>Tentar novamente</Button>
          <Button variant="contained" onClick={onRun} disabled={loading}>Gerar</Button>
          <Button color="error" variant="outlined" onClick={onDelete} disabled={loading}>Excluir</Button>
        </Box>
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.paper'
        }}
        display="grid"
        gap={2}
      >
        {editorialProfileData ? (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              p: 1.5,
              backgroundColor: 'background.default'
            }}
            display="grid"
            gap={0.75}
          >
            <Typography variant="subtitle1">Perfil Editorial</Typography>
            <Typography variant="body2" color="text.secondary">
              {editorialProfileData.label}
              {editorialProfileData.summary ? ` · ${editorialProfileData.summary}` : ''}
            </Typography>
            {editorialProfileData.narrativeStyle ? (
              <Typography variant="caption" color="text.secondary">
                Estilo adotado: {editorialProfileData.narrativeStyle}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {researchData || knowledgePackageData ? (
          <Tabs
            value={detailTab}
            onChange={(_, value) => setDetailTab(value)}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab value="tracking" label="Acompanhamento" />
            {researchData ? <Tab value="research" label="Pesquisa" /> : null}
            {knowledgePackageData ? <Tab value="knowledge" label="Pacote de Conhecimento" /> : null}
          </Tabs>
        ) : (
          <Typography variant="h6">Acompanhamento</Typography>
        )}

        {detailTab === 'tracking' || !researchData ? (
          <Box display="grid" gap={1.25}>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 12, borderRadius: 999 }} />
            <Typography variant="body2" color="text.secondary">
              {progress}% concluído
            </Typography>

            <Box display="grid" gap={1}>
              {groupedStages.map((stage) => {
                const meta = getStatusMeta(stage.status);
                return (
                  <Box
                    key={stage.key}
                    sx={{
                      border: '1px solid',
                      borderColor: stage.current ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      p: 1.5,
                      backgroundColor: stage.current ? 'action.selected' : 'background.default'
                    }}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    flexWrap="wrap"
                  >
                    <Box display="grid" gap={0.5}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{stage.label}</Typography>
                      {stage.status === 'failed' && stage.errorMessage ? (
                        <Typography variant="body2" color="error.main">
                          Motivo: {formatErrorLabel(stage.errorMessage)}
                        </Typography>
                      ) : null}
                      {stage.status === 'failed' && stage.lastErrorLog ? (
                        <Typography variant="caption" color="text.secondary">
                          Log: {stage.lastErrorLog}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography variant="body2" sx={{ color: meta.accent }}>{meta.label}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null}

        {researchData && detailTab === 'research' ? (
          <Box display="grid" gap={1.5}>
            <Typography variant="h6">Pesquisa</Typography>
            <Typography variant="body2" color="text.secondary">
              Nível de confiança: {researchData?.confidence?.overall != null ? `${Math.round(Number(researchData.confidence.overall))}/100` : '-'}
              {' '}· Fontes coletadas: {researchData.sources.length}
              {' '}· Fontes validadas: {researchData.validatedSources.length}
              {' '}· Fontes descartadas: {researchData.discardedSources.length}
              {' '}· Tempo gasto: {researchData?.metrics?.durationMs != null ? `${Math.round(Number(researchData.metrics.durationMs) / 1000)}s` : '-'}
            </Typography>
            {researchData?.qualityScores ? (
              <Typography variant="body2" color="text.secondary">
                Research Score: {researchData.qualityScores.overallResearchScore ?? '-'}
                {' '}· Source Quality: {researchData.qualityScores.sourceQuality ?? '-'}
                {' '}· Fact Confidence: {researchData.qualityScores.factConfidence ?? '-'}
                {' '}· Coverage: {researchData.qualityScores.coverageScore ?? '-'}
                {' '}· Knowledge Depth: {researchData.qualityScores.knowledgeDepth ?? '-'}
              </Typography>
            ) : null}
            {researchData?.report?.executiveSummary ? (
              <Typography variant="body2">
                Resumo executivo: {String(researchData.report.executiveSummary).trim()}
              </Typography>
            ) : null}
            {Array.isArray(researchData?.plan?.subtopics) && researchData.plan.subtopics.length ? (
              <Typography variant="body2">
                Plano de pesquisa: {researchData.plan.subtopics.slice(0, 12).join(' · ')}
              </Typography>
            ) : null}
            {researchData.facts.length ? (
              <Typography variant="body2">
                Principais fatos: {researchData.facts.slice(0, 5).map((item) => item?.title).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {researchData.timeline.length ? (
              <Typography variant="body2">
                Linha do tempo: {researchData.timeline.slice(0, 5).map((item) => {
                  const year = item?.year != null ? String(item.year) : String(item?.dateLabel || '').trim();
                  const title = String(item?.title || '').trim();
                  return [year, title].filter(Boolean).join(' - ');
                }).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {researchData.curiosities.length ? (
              <Typography variant="body2">
                Curiosidades: {researchData.curiosities.slice(0, 4).map((item) => item?.title || item?.summary).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {researchData.statistics.length ? (
              <Typography variant="body2">
                Estatísticas: {researchData.statistics.slice(0, 4).map((item) => item?.title || item?.value || item?.summary).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {researchData.validatedSources.length ? (
              <Typography variant="body2">
                Fontes principais: {researchData.validatedSources.slice(0, 4).map((item) => item?.title || item?.url).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {researchData.bibliography.length ? (
              <Typography variant="body2" color="text.secondary">
                Bibliografia registrada: {researchData.bibliography.length} itens
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {knowledgePackageData && detailTab === 'knowledge' ? (
          <Box display="grid" gap={1.5}>
            <Typography variant="h6">Pacote de Conhecimento</Typography>
            <Typography variant="body2" color="text.secondary">
              Versão: {knowledgePackageData.version ?? '-'}
              {' '}· Última atualização: {knowledgePackageData.updatedAt ? new Date(knowledgePackageData.updatedAt).toLocaleString('pt-BR') : '-'}
              {' '}· Nível de confiança: {knowledgePackageData?.confidence?.overall != null ? `${Math.round(Number(knowledgePackageData.confidence.overall))}/100` : '-'}
            </Typography>
            {knowledgePackageData?.qualityScores ? (
              <Typography variant="body2" color="text.secondary">
                Overall Knowledge Score: {knowledgePackageData.qualityScores.overallKnowledgeScore ?? '-'}
                {' '}· Completeness: {knowledgePackageData.qualityScores.knowledgeCompleteness ?? '-'}
                {' '}· Research Coverage: {knowledgePackageData.qualityScores.researchCoverage ?? '-'}
                {' '}· Entity Coverage: {knowledgePackageData.qualityScores.entityCoverage ?? '-'}
                {' '}· Timeline Consistency: {knowledgePackageData.qualityScores.timelineConsistency ?? '-'}
              </Typography>
            ) : null}
            {knowledgePackageData?.topicRelevanceAudit ? (
              <Typography variant="body2" color="text.secondary">
                Relevância temática: {knowledgePackageData.topicRelevanceAudit.sourcesAccepted ?? 0}/{knowledgePackageData.topicRelevanceAudit.sourcesEvaluated ?? 0} fontes aceitas
                {' '}· Rejeitadas por tema: {knowledgePackageData.topicRelevanceAudit.sourcesRejected ?? 0}
              </Typography>
            ) : null}
            {knowledgePackageData?.validation?.blockingIssues?.length ? (
              <Typography variant="body2" color="error.main">
                Pacote bloqueado: {knowledgePackageData.validation.blockingIssues.map((item) => displayText(item, ['code'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.executiveSummary ? (
              <Typography variant="body2">
                Resumo executivo: {String(knowledgePackageData.executiveSummary).trim()}
              </Typography>
            ) : null}
            {knowledgePackageData.timeline.length ? (
              <Typography variant="body2">
                Linha do tempo: {knowledgePackageData.timeline.slice(0, 5).map((item) => {
                  const year = item?.year != null ? String(item.year) : String(item?.dateLabel || item?.label || '').trim();
                  const title = String(item?.title || '').trim();
                  return [year, title].filter(Boolean).join(' - ');
                }).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.facts.length ? (
              <Typography variant="body2">
                Principais fatos: {knowledgePackageData.facts.slice(0, 5).map((item) => displayText(item, ['title', 'resumo', 'summary'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.curiosities.length ? (
              <Typography variant="body2">
                Curiosidades: {knowledgePackageData.curiosities.slice(0, 4).map((item) => displayText(item, ['title', 'summary'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.entities.length ? (
              <Typography variant="body2">
                Entidades: {knowledgePackageData.entities.slice(0, 6).map((item) => displayText(item, ['nome', 'name'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.locations.length ? (
              <Typography variant="body2">
                Locais: {knowledgePackageData.locations.slice(0, 5).map((item) => displayText(item, ['nome', 'name'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.characters.length ? (
              <Typography variant="body2">
                Personagens: {knowledgePackageData.characters.slice(0, 5).map((item) => displayText(item, ['nome', 'name'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.statistics.length ? (
              <Typography variant="body2">
                Estatísticas: {knowledgePackageData.statistics.slice(0, 4).map((item) => displayText(item, ['title', 'value', 'summary'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.glossary.length ? (
              <Typography variant="body2">
                Glossário: {knowledgePackageData.glossary.slice(0, 4).map((item) => displayText(item, ['term'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.storyHooks.length ? (
              <Typography variant="body2">
                Story Hooks: {knowledgePackageData.storyHooks.slice(0, 4).map((item) => displayText(item, ['title'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.visualOpportunities.length ? (
              <Typography variant="body2">
                Visual Opportunities: {knowledgePackageData.visualOpportunities.slice(0, 4).map((item) => displayText(item, ['title'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.sources.length ? (
              <Typography variant="body2">
                Fontes: {knowledgePackageData.sources.slice(0, 4).map((item) => displayText(item, ['title', 'url'])).filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {knowledgePackageData.bibliography.length ? (
              <Typography variant="body2" color="text.secondary">
                Bibliografia registrada: {knowledgePackageData.bibliography.length} itens
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {needsVoiceProfile ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'warning.main',
            borderRadius: 3,
            p: { xs: 2, md: 3 },
            backgroundColor: 'background.paper'
          }}
          display="grid"
          gap={2}
        >
          <Typography variant="h6">Narrador</Typography>
          <Typography variant="body2" color="text.secondary">
            Selecione a voz para continuar a geração da narração.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Narrador</InputLabel>
            <Select label="Narrador" value={voiceProfileIdDraft} onChange={(e) => setVoiceProfileIdDraft(e.target.value)}>
              <MenuItem value="">Selecionar</MenuItem>
              {voiceProfiles.map((voice) => (
                <MenuItem key={voice.id} value={String(voice.id)}>
                  {voice.name || `Narrador ${voice.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button variant="outlined" href="/voice-ai">Gerenciar vozes</Button>
            <Button variant="contained" onClick={onSaveVoice} disabled={loading}>Salvar e continuar</Button>
          </Box>
        </Box>
      ) : null}

      {needsManualScript ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'warning.main',
            borderRadius: 3,
            p: { xs: 2, md: 3 },
            backgroundColor: 'background.paper'
          }}
          display="grid"
          gap={2}
        >
          <Typography variant="h6">Roteiro</Typography>
          <Typography variant="body2" color="text.secondary">
            {isAutoScriptReview
              ? 'Revise o roteiro gerado automaticamente e aprove se estiver correto.'
              : 'Cole o JSON do roteiro para que o pipeline continue.'}
          </Typography>

          {!isAutoScriptReview ? (
            <TextField
              label="Prompt sugerido"
              fullWidth
              multiline
              minRows={6}
              value={manualPrompt}
              onChange={(e) => setManualPrompt(e.target.value)}
            />
          ) : null}

          <TextField
            label="Roteiro JSON"
            fullWidth
            multiline
            minRows={12}
            value={manualScriptText}
            onChange={(e) => setManualScriptText(e.target.value)}
          />

          {scriptStats ? (
            <Typography variant="body2" color="text.secondary">
              {scriptStats.words} palavras · {scriptStats.chars} caracteres
            </Typography>
          ) : null}

          <Box display="flex" gap={1} flexWrap="wrap">
            {isAutoScriptReview ? (
              <Button variant="outlined" onClick={onApproveScript} disabled={loading || manualLoading}>
                Aprovar sem alterações
              </Button>
            ) : null}
            <Button variant="contained" onClick={onSaveManualScript} disabled={loading || manualLoading || !manualScriptText.trim()}>
              Salvar roteiro e continuar
            </Button>
          </Box>
        </Box>
      ) : null}

      {needsEditorialReview ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'warning.main',
            borderRadius: 3,
            p: { xs: 2, md: 3 },
            backgroundColor: 'background.paper'
          }}
          display="grid"
          gap={2}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
            <Box display="grid" gap={0.5}>
              <Typography variant="h6">Revisão Editorial</Typography>
              <Typography variant="body2" color="text.secondary">
                Assista ao vídeo, revise a thumbnail e aprove ou recuse a produção antes da publicação.
                {editorialStageMeta.trigger === 'upload_youtube_approval_gate'
                  ? ' · Bloqueio antes do upload para YouTube.'
                  : ''}
              </Typography>
              {editorialStageMeta.approvalStatus?.status ? (
                <Chip
                  size="small"
                  variant="outlined"
                  color={String(editorialStageMeta.approvalStatus.status).toLowerCase() === 'aprovado' || String(editorialStageMeta.approvalStatus.status).toLowerCase() === 'approved'
                    ? 'success'
                    : String(editorialStageMeta.approvalStatus.status).toLowerCase() === 'rejeitar' || String(editorialStageMeta.approvalStatus.status).toLowerCase() === 'rejected'
                      ? 'error'
                      : 'warning'}
                  label={`Decisão: ${String(editorialStageMeta.approvalStatus.status || '')}`}
                />
              ) : null}
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              {editorialStageMeta.overallScore != null ? (
                <Chip
                  label={`Score: ${editorialStageMeta.overallScore}`}
                  color={editorialStageMeta.overallScore >= 70 ? 'success' : editorialStageMeta.overallScore >= 50 ? 'warning' : 'error'}
                />
              ) : null}
              {editorialStageMeta.durationSeconds != null ? (
                <Chip label={`Duração: ${Math.round(Number(editorialStageMeta.durationSeconds) / 60)}m ${Number(editorialStageMeta.durationSeconds) % 60}s`} />
              ) : null}
              {editorialStageMeta.resolution ? <Chip label={`Resolução: ${editorialStageMeta.resolution}`} /> : null}
              {editorialStageMeta.hasSubtitles ? <Chip label="Legendado" color="success" variant="outlined" /> : null}
            </Box>
          </Box>

          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 360px' }} gap={2}>
            {editorialStageMeta.resolvedVideoUrl ? (
              <Box
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: '#000'
                }}
              >
                <video
                  controls
                  preload="metadata"
                  playsInline
                  crossOrigin="anonymous"
                  controlsList="nodownload noremoteplayback"
                  disablePictureInPicture
                  style={{ width: '100%', display: 'block', background: '#000' }}
                  src={editorialStageMeta.resolvedVideoUrl}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  borderRadius: 3,
                  border: '1px dashed',
                  borderColor: 'divider',
                  p: 4,
                  backgroundColor: 'background.default',
                  display: 'grid',
                  placeItems: 'center',
                  minHeight: 220
                }}
              >
                <Box display="grid" gap={0.75} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Vídeo ainda não disponível
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Aguarde a geração do vídeo para revisar.
                  </Typography>
                </Box>
              </Box>
            )}

            <Box display="grid" gap={2}>
              <Box display="grid" gap={0.75}>
                <Typography variant="subtitle2" color="text.secondary">Thumbnail</Typography>
                {editorialStageMeta.resolvedThumbUrl ? (
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <img
                      src={editorialStageMeta.resolvedThumbUrl}
                      alt="Thumbnail do vídeo"
                      style={{ width: '100%', display: 'block', objectFit: 'cover', aspectRatio: '16 / 9' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      p: 3,
                      backgroundColor: 'background.default',
                      display: 'grid',
                      placeItems: 'center',
                      aspectRatio: '16 / 9'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Sem thumbnail
                    </Typography>
                  </Box>
                )}
                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/jpg"
                  style={{ display: 'none' }}
                  onChange={onReplaceThumbnail}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={uploadingThumbnail || !editorialStageMeta.canDecide}
                  onClick={() => thumbnailInputRef.current?.click()}
                >
                  {uploadingThumbnail ? 'Enviando...' : 'Substituir thumbnail'}
                </Button>
              </Box>
              {editorialStageMeta.issues.length ? (
                <Box display="grid" gap={0.5}>
                  <Typography variant="subtitle2" color="text.secondary">Problemas detectados ({editorialStageMeta.issues.length})</Typography>
                  <Box display="grid" gap={0.5} sx={{ maxHeight: 160, overflowY: 'auto', pr: 0.5 }}>
                    {editorialStageMeta.issues.slice(0, 12).map((issue) => (
                      <Chip
                        key={issue.key}
                        size="small"
                        variant="outlined"
                        color={String(issue.severity || '').toLowerCase() === 'error' || String(issue.severity || '').toLowerCase() === 'critical'
                          ? 'error'
                          : String(issue.severity || '').toLowerCase() === 'warning'
                            ? 'warning'
                            : 'default'}
                        label={String(issue.label || '').slice(0, 160)}
                      />
                    ))}
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              size="small"
              variant="outlined"
              label={`Video Asset: ${editorialStageMeta.debugVideoAssetId ?? 'não encontrado'} · preview: ${editorialStageMeta.debugPreviewVideo}`}
              color={editorialStageMeta.debugVideoAssetId ? 'info' : 'default'}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Thumbnail Asset: ${editorialStageMeta.debugThumbAssetId ?? 'não encontrado'} · preview: ${editorialStageMeta.debugPreviewThumb}`}
              color={editorialStageMeta.debugThumbAssetId ? 'info' : 'default'}
            />
          </Box>

          <Box display="grid" gap={1.5}>
            <Typography variant="subtitle2" color="text.secondary">Anotações adicionais (opcional)</Typography>
            <TextField
              label="Comentários"
              fullWidth
              multiline
              minRows={3}
              value={editorialComment}
              onChange={(e) => setEditorialComment(e.target.value)}
              placeholder="Descreva observações que podem ser úteis para a equipe."
            />
            <TextField
              label="Motivo da recusa (obrigatório ao recusar)"
              fullWidth
              multiline
              minRows={2}
              value={editorialReason}
              onChange={(e) => setEditorialReason(e.target.value)}
              placeholder="Ex.: Roteiro com dados incorretos, vídeo com artefatos visuais, áudio com ruído, etc."
            />
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            <Button
              variant="contained"
              color="error"
              onClick={onRejectEditorial}
              disabled={loading || !!editorialSubmitting}
            >
              {editorialSubmitting === 'reject' ? 'Processando...' : 'Recusar'}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={onApproveEditorial}
              disabled={loading || !!editorialSubmitting}
            >
              {editorialSubmitting === 'approve' ? 'Processando...' : 'Aprovar'}
            </Button>
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.paper'
        }}
        display="grid"
        gap={1}
      >
        <Typography variant="h6">Última atualização</Typography>
        <Typography variant="body2" color="text.secondary">
          Status: {prod?.status || '-'} · atualizado em {fmtDate(prod?.updatedAt)}
        </Typography>
        {visibleErrorMessage ? (
          <Typography variant="body2" color="error.main">
            {formatErrorLabel(visibleErrorMessage)}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
